import {
  callbackDataFromUrl,
  parseImportCallback,
  importAuthMessages,
} from "./importAuth";
import { clearImportSession } from "@components/Onboarding/importSession";
import LScreen from "@components/LScreen";
import LBox from "@components/LBox";
import LText from "@components/LText";
import LIcon, { IconName } from "@components/LIcon";
import LPressable from "@components/LPressable";
import * as WebBrowser from "expo-web-browser";
import { router, useLocalSearchParams } from "expo-router";
import { useMusicSyncGetAuthUrl } from "@hooks/trpc/useMusicSync";
import { Platform, StyleSheet, FlatList } from "react-native";
import { StreamingService } from "./constants";
import LButton from "@components/LButton";
import { useState, useEffect, useRef } from "react";
import { trackOnb, trackDirectImport } from "./analytics/trackOnboarding";
import { useOnboardingStore } from "./store/onboardingStore";
import { theme } from "@theme";
import { useFlashingBorder } from "./useFlashingBorder";
import { toast } from "sonner-native";

const STREAMING_SERVICES_LIST: {
  type: StreamingService;
  name: string;
  icon: IconName;
}[] = [
  {
    type: "spotify",
    name: "Spotify",
    icon: "spotify",
  },
  // {
  //   type: "youtube",
  //   name: "YT Music",
  //   icon: "yt_music",
  // },
  {
    type: "appleMusic",
    name: "Apple Music",
    icon: "apple_music",
  },
  {
    type: "soundCloud",
    name: "SoundCloud",
    icon: "soundcloud",
  },
] as const;

const LinkStreamingService = () => {
  const { source = "intro" } = useLocalSearchParams<{ source?: string }>();
  const pending = useRef(false);
  const [busy, setBusy] = useState(false);
  const [selectedService, setSelectedService] =
    useState<StreamingService | null>(null);
  const { mutateAsync: getAuthUrl } = useMusicSyncGetAuthUrl();
  const journey = useOnboardingStore((s) => s.journey);
  const setJourney = useOnboardingStore((s) => s.setJourney);

  useEffect(() => {
    // direct-import: user came from settings, no intro screen set the journey
    if (source === "settings" && !journey) {
      setJourney("direct-import");
    }
  }, [source, journey, setJourney]);

  const j = source === "settings" ? "direct-import" : journey ?? "signup";
  const trackFn = (
    screen: string,
    action: string,
    props?: Record<string, unknown>,
  ) =>
    j === "direct-import"
      ? trackDirectImport(screen, action, props)
      : trackOnb(j, screen, action, props);

  useEffect(() => {
    trackFn("select-dsp", "view");
  }, [j]);

  const handleSync = async (service: StreamingService) => {
    if (pending.current) return;
    pending.current = true;
    setBusy(true);
    try {
      const returnUrl = `${Platform.OS === "web" ? window.location.origin : "lsn:/"}/onboarding/callback`;

      await clearImportSession();
      const { authUrl } = await getAuthUrl({
        service,
        returnUrl,
      });

      trackFn("oauth", "open", { dsp: service });

      if (Platform.OS === "web") {
        window.location.href = authUrl;
        return;
      }

      const res = await WebBrowser.openAuthSessionAsync(authUrl, returnUrl);

      if (res?.type === "success" && res.url) {
        const data64 = callbackDataFromUrl(res.url, returnUrl);
        const callback = parseImportCallback(data64, service);
        if (!callback.ok) {
          trackFn("oauth", "error", {
            dsp: service,
            error_code: callback.code,
          });
          toast.error(importAuthMessages[callback.code]);
          return;
        }
        trackFn("oauth", "success", { dsp: service });
        router.replace({
          pathname: "/onboarding/callback",
          params: { data64: data64! },
        });
      } else if (res?.type === "cancel" || res?.type === "dismiss") {
        const code =
          res.type === "cancel" ? "user_cancelled" : "browser_dismissed";
        trackFn("oauth", res.type === "cancel" ? "cancel" : "error", {
          dsp: service,
          error_code: code,
        });
        toast.error(importAuthMessages[code]);
      }
    } catch {
      trackFn("oauth", "error", {
        dsp: service,
        error_code: "connection_failed",
      });
      toast.error("We couldn’t connect. Please try again.");
    } finally {
      pending.current = false;
      setBusy(false);
    }
  };

  return (
    <LScreen safeArea alignItems="center" pt="l3">
      <LText mb="m2" textAlign="center" variant="h2" mt="m2">
        Link your previous streaming service for personalized recommendations.
      </LText>
      <LBox flexDirection="row" flexWrap="wrap" gap="s3">
        <FlatList
          data={STREAMING_SERVICES_LIST}
          numColumns={3}
          keyExtractor={(item) => item.type}
          contentContainerStyle={{
            gap: theme.spacing.s3,
          }}
          columnWrapperStyle={{
            gap: theme.spacing.s3,
          }}
          renderItem={({ item: service, index }) => (
            <StreamingServiceItem
              enableBorderFlashing={index === 0 && selectedService === null}
              service={service}
              isSelected={selectedService === service.type}
              onPress={() => {
                trackFn("select-dsp", "dsp-pressed", { dsp: service.type });
                if (!busy) setSelectedService(service.type);
              }}
            />
          )}
        />
      </LBox>

      <LBox width="100%" gap="m1" pb="l3" position="absolute" bottom={0}>
        <LButton
          sentryEventName={null}
          label={busy ? "CONNECTING…" : "CONTINUE"}
          enabled={!busy}
          onPress={() => {
            if (!selectedService) {
              toast.error("Please select a streaming service");
              return;
            }
            trackFn("select-dsp", "continue-pressed", { dsp: selectedService });
            handleSync(selectedService).catch(console.error);
          }}
          justifyContent="center"
          borderRadius={"max"}
        />
        <LPressable
          sentryEventName={null}
          onPress={() => {
            trackFn("select-dsp", "back-pressed");
            router.back();
          }}
        >
          <LText variant="bodyM2" color="whiteTranslucent50" textAlign="center">
            Go Back
          </LText>
        </LPressable>
      </LBox>
    </LScreen>
  );
};

export default LinkStreamingService;

const StreamingServiceItem = ({
  enableBorderFlashing,
  service,
  isSelected,
  onPress,
}: {
  enableBorderFlashing: boolean;
  service: (typeof STREAMING_SERVICES_LIST)[number];
  isSelected: boolean;
  onPress: () => void;
}) => {
  const animatedBorderStyle = useFlashingBorder(enableBorderFlashing);

  return (
    <LPressable
      sentryEventName={null}
      onPress={onPress}
      style={{
        flex: 1 / 3,
      }}
    >
      <LBox
        px="s1"
        py="l1"
        justifyContent="center"
        alignItems="center"
        borderRadius="m"
        backgroundColor="softNight"
        gap="s4"
        borderWidth={1}
        borderBottomWidth={3}
        borderColor={isSelected ? "white" : "whiteTranslucent20"}
        flex={1}
        style={animatedBorderStyle}
      >
        {isSelected && (
          <LBox
            backgroundColor={["lead", "softNight", "transparent"]}
            style={StyleSheet.absoluteFill}
            borderRadius="m"
            opacity={0.1}
          />
        )}
        {isSelected && (
          <LBox position="absolute" top={2} right={2} zIndex={10}>
            <LIcon name="check" size={24} />
          </LBox>
        )}
        <LIcon name={service.icon} size={44} />
        <LText variant="buttonMedium" color="white" textAlign="center">
          {service.name}
        </LText>
      </LBox>
    </LPressable>
  );
};
